/**
 * <s-product-gallery> — one-at-a-time product media carousel for every viewport.
 *
 * The scroll-snap track stays usable by swipe or trackpad without JavaScript; this
 * component adds arrow, thumbnail and counter sync.
 *
 * Required markup:
 *   [data-gallery-track]               — horizontally scrolling snap track.
 *   [data-gallery-slide]               — one per media item, inside the track.
 *   button[commandfor=<this id>]       — command "--prev", "--next", or "--go" with data-index.
 *   [data-gallery-prev] / [data-gallery-next] — arrow buttons, disabled at the ends.
 *   [data-gallery-thumbs] [data-gallery-thumb][data-index] — optional thumbnail strip.
 *   [data-gallery-status][data-template] — optional counter; template holds [current] and [total].
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });

    const track = this.#track;
    if (!track) return;

    // The visible slide drives state so swipes, trackpads and arrows stay in sync.
    this.#observer = new IntersectionObserver(this.#handleIntersect, { root: track, threshold: 0.6 });
    for (const slide of this.#slides) this.#observer.observe(slide);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    return [...this.querySelectorAll('[data-gallery-slide]')];
  }

  #handleIntersect = (entries) => {
    const slides = this.#slides;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = slides.indexOf(entry.target);
      if (index !== -1 && index !== this.#index) {
        this.#index = index;
        this.#update();
      }
    }
  };

  #handleCommand = (event) => {
    let target;
    if (event.command === '--prev') target = this.#index - 1;
    else if (event.command === '--next') target = this.#index + 1;
    else if (event.command === '--go') target = Number(event.source?.dataset.index);
    else return;

    if (Number.isInteger(target)) this.#goTo(target);
  };

  #goTo(requested) {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    const index = Math.max(0, Math.min(slides.length - 1, requested));
    // Scroll only the track by its physical offset, so the page never jumps and RTL tracks work.
    const offset = slides[index].getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: offset, behavior: this.#behavior });

    this.#index = index;
    this.#update();
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #update() {
    const total = this.#slides.length;
    const index = this.#index;

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= total - 1;

    const status = this.querySelector('[data-gallery-status]');
    if (status?.dataset.template) {
      status.textContent = status.dataset.template
        .replace('[current]', String(index + 1))
        .replace('[total]', String(total));
    }

    const strip = this.querySelector('[data-gallery-thumbs]');
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const active = Number(thumb.dataset.index) === index;
      thumb.toggleAttribute('aria-current', active);
      if (active && strip) {
        // Keep the active thumbnail visible inside the strip without scrolling the page.
        const stripBox = strip.getBoundingClientRect();
        const thumbBox = thumb.getBoundingClientRect();
        if (thumbBox.left < stripBox.left || thumbBox.right > stripBox.right) {
          const delta = thumbBox.left - stripBox.left - (stripBox.width - thumbBox.width) / 2;
          strip.scrollBy({ left: delta, behavior: this.#behavior });
        }
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
